import { createContext, useContext, ReactNode, FC, Dispatch, SetStateAction } from 'react'
import {
  ChartData,
  ConfiguredCPI,
  CPIFrequency,
  CPILibraryItem,
  CPIManifest,
  CPISeverity,
  CPITimePeriod,
  ModelManifest,
  IntegrationInstance,
  MetricManifest,
  PredictionChartData,
  PredictionData,
  SnowflakeChartData,
  CustomLabelI
} from 'models'
import { User } from '@frontegg/redux-store'
import { FormValues } from '../ui/pages/library/library.constants'
import { DevicesTabs } from '../ui/pages/cpi-details/useLibraryDetailsData'

interface LibraryDetailsContextType {
  authUser: User | null | undefined

  cpi: CPILibraryItem | null
  manifestData: CPIManifest | null
  fw: string
  cpiConfiguration: ConfiguredCPI | null

  chartData: ChartData | SnowflakeChartData | null
  setChartData: Dispatch<SetStateAction<ChartData | SnowflakeChartData | null>>
  active: boolean
  criticalSla: number | undefined
  setCriticalSla: Dispatch<SetStateAction<number | undefined>>
  showMessage: boolean
  setShowMessage: Dispatch<SetStateAction<boolean>>
  recalculatingCpiName: string | null
  setRecalculatingCpiName: Dispatch<SetStateAction<string | null>>
  cpiSupportedAndUserConfiguredList: IntegrationInstance[]
  userConfiguredIntegrations: IntegrationInstance[]
  healthDirection: string
  activeSources: IntegrationInstance[]
  predictionData: PredictionChartData | null
  setPredictionData: Dispatch<SetStateAction<PredictionChartData | null>>
  fetchPredictionData: (cpiName: string, severity: CPISeverity, dataSourceName: string) => Promise<PredictionData[]>
  setDataSourceValue: Dispatch<SetStateAction<string>>
  timePeriodValues: Array<CPITimePeriod | string>
  potentialSources: IntegrationInstance[]

  setHealthDirection: Dispatch<SetStateAction<string>>
  redirectToMode: string
  setRedirectToMode: Dispatch<SetStateAction<string>>
  setCurrentSeverityStatus: Dispatch<SetStateAction<'healthy' | 'problematic'>>
  customTimePeriodValue: string
  frequencyValues: Array<CPIFrequency>
  dataSourceOptions: IntegrationInstance[]
  dataSourceValue: string
  setCustomTimePeriodValue: Dispatch<SetStateAction<string>>
  renderingChart: boolean
  setRenderingChart: Dispatch<SetStateAction<boolean>>
  currentSeverityStatus: 'healthy' | 'problematic'
  initialLoad: boolean
  setInitialLoad: Dispatch<SetStateAction<boolean>>
  emptyInfoTooltipOpen: boolean
  setEmptyInfoTooltipOpen: Dispatch<SetStateAction<boolean>>

  selectedSeverity: CPISeverity
  setSelectedSeverity: Dispatch<SetStateAction<CPISeverity>>
  selectedTimePeriod: CPITimePeriod
  setSelectedTimePeriod: Dispatch<SetStateAction<CPITimePeriod>>
  selectedFrequency: CPIFrequency
  setSelectedFrequency: Dispatch<SetStateAction<CPIFrequency>>
  selectedDataSourceIds: string[]
  setSelectedDataSourceIds: Dispatch<SetStateAction<string[]>>

  defaultValues: FormValues
  selectedType: string
  setSelectedType: Dispatch<SetStateAction<string>>
  drawerOpen: boolean
  setDrawerOpen: Dispatch<SetStateAction<boolean>>
  labelDrawerOpen: boolean
  setLabelDrawerOpen: Dispatch<SetStateAction<boolean>>
  handleSlaDescriptionTooltipClick: () => void

  setInitialQueryLoaded: Dispatch<SetStateAction<boolean>>
  predictionModalOpen: boolean
  showBenchmarkingTab: boolean
  tabValue: DevicesTabs
  setTabValue: Dispatch<SetStateAction<DevicesTabs>>
  handleEmptyInfoClick: () => void
  predictionFetched: boolean
  isSnowflake: boolean
  mode: string
  editLabel: CustomLabelI
  setEditLabel: Dispatch<SetStateAction<CustomLabelI>>
  setMode: Dispatch<SetStateAction<string>>
  stripes?: number | null
  setStripes: Dispatch<SetStateAction<number | null>>
  isMediumHeight: boolean
  deactivateCpiV1: boolean
  setDeactivateCpiV1: Dispatch<SetStateAction<boolean>>

  notConfiguredInstancesApplicableToCpi: IntegrationInstance[]
  fetchingInterval: number
  setFetchingInterval: Dispatch<SetStateAction<number>>
  handlePredictionModalOpen: () => void
  hasPredictionsEnabled: boolean
  predictionModelAvailable: boolean
  refetchPredictionData: ({
    thisCpiName,
    severity,
    selectedSources,
    timePeriod
  }: {
    thisCpiName: string
    severity?: CPISeverity
    selectedSources?: string[]
    timePeriod?: CPITimePeriod
  }) => Promise<ChartData | null>
  daysUntilDataAvailable: number
  handlePredictionModalClose: (hideNotification?: boolean) => void

  recentInitialLoad: boolean
  defaultRecentValueDate: {
    date: string
    value: string
    denominator: string
    lastValue: string
  }
  setDefaultRecentValueDate: (value: { date: string; value: string; denominator: string; lastValue: string }) => void

  snowflakeManifest: ModelManifest | null
  metricManifest?: MetricManifest
  triggerTrendsRefetch: () => void
  trendsRefetchTrigger: number
  manifestV2: boolean
}

const LibraryDetailsContext = createContext<LibraryDetailsContextType | undefined>(undefined)

interface LibraryDetailsProviderProps {
  children: ReactNode
  value: LibraryDetailsContextType
}

export const LibraryDetailsProvider: FC<LibraryDetailsProviderProps> = ({ children, value }) => {
  return <LibraryDetailsContext.Provider value={value}>{children}</LibraryDetailsContext.Provider>
}

export const useLibraryDetails = () => {
  const context = useContext(LibraryDetailsContext)

  if (context === undefined) {
    throw new Error('useLibraryDetails must be used within a LibraryDetailsProvider')
  }

  return context
}
